"use client";

import { useState, useCallback, useEffect } from "react";
import Link from "next/link";
import { useRouter, useSearchParams } from "next/navigation";
import {
  ROLES_TREE,
  LOCATIONS_TREE,
  LISTING_PERKS,
  getAllLeafIds,
  getNodeCheckState,
  type TreeNode,
} from "@/lib/taxonomy";
import { JobAlertSignup } from "@/app/components/JobAlertSignup";
import { CascadingTree } from "@/app/components/CascadingTree";
import type { UserRole } from "@/lib/auth";
import type { CandidateListingState } from "@/lib/marketplace/candidate-listing-state";
import type { ListingWithCompany } from "@/lib/marketplace/listing-types";
import type { PublicEmployerJobPage } from "@/lib/marketplace/employer-job-pages";
import { DesignatedEmployerModule } from "@/app/_components/marketplace/DesignatedEmployerModule";
import { ListingInteractionGrid } from "@/app/_components/marketplace/ListingInteractionGrid";

const SEASON_GROUP_CHIPS = [
  { id: "snow", label: "Winter", icon: "\u2744\uFE0F" },
  { id: "sun", label: "Summer", icon: "\u2600\uFE0F" },
  { id: "festival", label: "Festivals", icon: "\uD83C\uDFAA" },
] as const;

function buildTreeChips(
  tree: TreeNode[],
  selected: Set<string>
): { key: string; label: string; leafIds: string[] }[] {
  const chips: { key: string; label: string; leafIds: string[] }[] = [];
  function walk(nodes: TreeNode[]) {
    for (const node of nodes) {
      const state = getNodeCheckState(node, selected);
      if (state === "unchecked") continue;
      if (state === "checked" && node.children && node.children.length > 0) {
        chips.push({
          key: node.id,
          label: node.label,
          leafIds: getAllLeafIds(node),
        });
      } else if (node.children && node.children.length > 0) {
        walk(node.children);
      } else if (selected.has(node.id)) {
        chips.push({ key: node.id, label: node.label, leafIds: [node.id] });
      }
    }
  }
  walk(tree);
  return chips;
}

// ═══ FILTER SIDEBAR ═══

function FilterSidebar({
  roleIds,
  onRolesChange,
  locationIds,
  onLocationsChange,
  perkIds,
  onPerkToggle,
  totalCount,
  filteredCount,
  hasAnyFilter,
  onClearAll,
  onClose,
}: {
  roleIds: Set<string>;
  onRolesChange: (s: Set<string>) => void;
  locationIds: Set<string>;
  onLocationsChange: (s: Set<string>) => void;
  perkIds: Set<string>;
  onPerkToggle: (id: string) => void;
  totalCount: number;
  filteredCount: number;
  hasAnyFilter: boolean;
  onClearAll: () => void;
  onClose?: () => void;
}) {
  const isMobile = !!onClose;

  return (
    <div className="flex flex-col h-full">
      <div className="flex justify-between items-start mb-1">
        <h3 className="font-serif text-xl">Find Jobs</h3>
        {onClose && (
          <button
            onClick={onClose}
            className="md:hidden w-8 h-8 rounded-sm bg-warm-bg border-none text-lg cursor-pointer flex items-center justify-center"
            aria-label="Close filters"
          >
            &times;
          </button>
        )}
      </div>
      {!isMobile && (
        <p className="text-xs text-text-muted mb-5">
          {hasAnyFilter
            ? `${filteredCount} listing${filteredCount !== 1 ? "s" : ""} matching`
            : `${totalCount} listing${totalCount !== 1 ? "s" : ""}`}
        </p>
      )}

      {hasAnyFilter && (
        <button
          onClick={() => {
            onClearAll();
            if (onClose) onClose();
          }}
          className="w-full text-[11px] text-text-muted hover:text-gold transition-colors bg-transparent border-none cursor-pointer font-sans mb-4 text-right"
        >
          Clear all filters
        </button>
      )}

      <div className="flex-1 overflow-y-auto space-y-5 pb-4">
        {/* Roles */}
        <CascadingTree
          tree={ROLES_TREE}
          selected={roleIds}
          onChange={onRolesChange}
          label="Roles"
          maxHeight="240px"
          searchable
        />

        {/* Locations */}
        <CascadingTree
          tree={LOCATIONS_TREE}
          selected={locationIds}
          onChange={onLocationsChange}
          label="Locations"
          maxHeight="240px"
          searchable
        />

        {/* Perks */}
        <div>
          <label className="text-[12px] font-semibold text-text-muted uppercase tracking-[0.6px] block mb-2">
            Perks
          </label>
          <div className="flex flex-col gap-2">
            {LISTING_PERKS.map((perk) => (
              <label
                key={perk.id}
                className="flex items-center gap-2.5 cursor-pointer"
              >
                <input
                  type="checkbox"
                  checked={perkIds.has(perk.id)}
                  onChange={() => onPerkToggle(perk.id)}
                  className="w-4 h-4 accent-gold cursor-pointer shrink-0"
                />
                <span className="text-sm text-text-body">{perk.label}</span>
              </label>
            ))}
          </div>
        </div>
      </div>

      {/* Mobile apply button */}
      {onClose && (
        <button
          onClick={onClose}
          className="md:hidden w-full py-3 rounded-sm text-[14px] font-semibold bg-gold text-white border-none cursor-pointer font-sans mt-4 hover:bg-gold-light transition-all"
        >
          Show {filteredCount} listing{filteredCount !== 1 ? "s" : ""}
        </button>
      )}
    </div>
  );
}

// ═══ MAIN COMPONENT ═══

export function JobsContent({
  initialListings,
  userRole,
  totalCount,
  candidateState,
  candidateCount = 0,
  featuredEmployers,
  embeddedAfterHub = false,
}: {
  initialListings: ListingWithCompany[];
  userRole: UserRole;
  totalCount: number;
  candidateState: CandidateListingState;
  candidateCount?: number;
  featuredEmployers: PublicEmployerJobPage[];
  embeddedAfterHub?: boolean;
}) {
  const router = useRouter();
  const searchParams = useSearchParams();
  const [mobileFiltersOpen, setMobileFiltersOpen] = useState(false);

  // Derive filter state from URL
  const seasonFilter = searchParams.get("season") || "";
  const currentQuery = searchParams.toString();
  const jobsReturnPath = currentQuery ? `/jobs?${currentQuery}` : "/jobs";
  const roleIds = searchParams.get("role")
    ? new Set(searchParams.get("role")!.split(","))
    : new Set<string>();
  const locationIds = searchParams.get("location")
    ? new Set(searchParams.get("location")!.split(","))
    : new Set<string>();
  const perkIds = searchParams.get("perks")
    ? new Set(searchParams.get("perks")!.split(","))
    : new Set<string>();

  const hasAnyFilter =
    seasonFilter !== "" ||
    roleIds.size > 0 ||
    locationIds.size > 0 ||
    perkIds.size > 0;

  const filterCount =
    (seasonFilter ? 1 : 0) + roleIds.size + locationIds.size + perkIds.size;

  const updateParams = useCallback(
    (updates: Record<string, string | null>) => {
      const params = new URLSearchParams(searchParams.toString());
      for (const [key, value] of Object.entries(updates)) {
        if (value) {
          params.set(key, value);
        } else {
          params.delete(key);
        }
      }
      const qs = params.toString();
      router.replace(`/jobs${qs ? `?${qs}` : ""}`, { scroll: false });
    },
    [router, searchParams]
  );

  function handleSeasonChange(value: string) {
    updateParams({ season: value || null });
  }

  function handleRolesChange(selected: Set<string>) {
    const arr = Array.from(selected);
    updateParams({ role: arr.length > 0 ? arr.join(",") : null });
  }

  function handleLocationsChange(selected: Set<string>) {
    const arr = Array.from(selected);
    updateParams({ location: arr.length > 0 ? arr.join(",") : null });
  }

  function handlePerkToggle(perkId: string) {
    const next = new Set(perkIds);
    if (next.has(perkId)) {
      next.delete(perkId);
    } else {
      next.add(perkId);
    }
    const arr = Array.from(next);
    updateParams({ perks: arr.length > 0 ? arr.join(",") : null });
  }

  function clearAllFilters() {
    router.replace("/jobs", { scroll: false });
  }

  // Lock body scroll when the mobile filter drawer is open.
  useEffect(() => {
    document.body.style.overflow = mobileFiltersOpen ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [mobileFiltersOpen]);

  // Build filter chips
  const roleChips = buildTreeChips(ROLES_TREE, roleIds);
  const locationChips = buildTreeChips(LOCATIONS_TREE, locationIds);

  const listings = initialListings;

  // Determine active season group from season filter
  const activeGroup = SEASON_GROUP_CHIPS.find((g) =>
    g.id === seasonFilter
  )?.id ?? "";

  const sidebarProps = {
    roleIds,
    onRolesChange: handleRolesChange,
    locationIds,
    onLocationsChange: handleLocationsChange,
    perkIds,
    onPerkToggle: handlePerkToggle,
    totalCount,
    filteredCount: listings.length,
    hasAnyFilter,
    onClearAll: clearAllFilters,
  };

  // Show job alerts for all non-employer users
  const showJobAlerts = userRole !== "employer";

  return (
    <>
      <div
        className={`grid min-h-screen grid-cols-1 md:grid-cols-[280px_minmax(0,1fr)] ${
          embeddedAfterHub ? "" : "-mt-16 pt-16"
        }`}
      >
        {/* Main content. It comes before the filter sidebar in the HTML so the
            listings are the first thing crawlers and screen readers reach; grid
            placement keeps the sidebar in the left column on desktop. */}
        <div className="p-5 md:px-9 md:py-7 md:col-start-2 md:row-start-1">
          {/* Keyboard and screen-reader users reach the listings first, so
              give them a way to jump to the desktop filters. */}
          <a
            href="#job-filters"
            className="hidden md:block sr-only focus:not-sr-only focus:mb-4 focus:w-fit focus:rounded-sm focus:bg-navy focus:px-4 focus:py-3 focus:text-sm focus:font-semibold focus:text-white"
          >
            Skip to filters
          </a>
          {/* Header */}
          <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4 mb-4">
            <div>
              {embeddedAfterHub ? (
                <h2 className="font-heading font-bold text-[24px] sm:text-[28px] text-navy leading-tight mb-1">
                  Browse jobs
                </h2>
              ) : (
                <h1 className="font-serif text-[28px] sm:text-[36px] text-navy leading-tight mb-1">
                  Seasonal Jobs
                </h1>
              )}
              {!embeddedAfterHub && <p className="text-[14px] text-text-muted max-w-[480px] leading-relaxed">
                Browse job listings from employers hiring across ski, yacht,
                festival and summer seasons.
              </p>}
              {userRole !== "candidate" && <Link
                href={userRole === "employer" ? "/employer/listings/new" : "/employer-signup"}
                className="md:hidden inline-flex mt-3 px-5 py-2.5 rounded-sm text-[14px] font-semibold bg-gold text-white no-underline font-sans transition-all hover:bg-gold-light"
              >
                Post a Job for Free
              </Link>}
            </div>
            {userRole !== "candidate" && <Link
              href={userRole === "employer" ? "/employer/listings/new" : "/employer-signup"}
              className="hidden md:inline-flex shrink-0 px-5 py-2.5 rounded-sm text-[14px] font-semibold bg-gold text-white no-underline font-sans transition-all hover:bg-gold-light self-start"
            >
              Post a Job for Free
            </Link>}
          </div>

          <DesignatedEmployerModule employers={featuredEmployers} />

          {/* Mobile filter toggle */}
          <div className="md:hidden sticky top-16 z-50 bg-warm-bg/95 backdrop-blur-sm border-b border-border-light -mx-5 px-5 py-3 mb-4">
            <div className="flex justify-between items-center gap-3">
              <span className="text-sm text-text-muted">
                <strong className="text-charcoal">{listings.length}</strong>{" "}
                listing{listings.length !== 1 ? "s" : ""}
                {hasAnyFilter ? " matching" : ""}
              </span>
              <button
                type="button"
                onClick={() => setMobileFiltersOpen(true)}
                className="min-h-11 px-4 py-2 rounded-lg text-[14px] font-semibold bg-gold text-white border-none cursor-pointer font-sans transition-colors hover:bg-gold-light flex items-center justify-center gap-2"
              >
                <svg
                  width="14"
                  height="14"
                  viewBox="0 0 14 14"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="1.5"
                >
                  <line x1="1" y1="3" x2="13" y2="3" />
                  <line x1="3" y1="7" x2="11" y2="7" />
                  <line x1="5" y1="11" x2="9" y2="11" />
                </svg>
                Filters
                {filterCount > 0 && (
                  <span className="w-5 h-5 bg-white/25 rounded-full text-[10px] font-bold flex items-center justify-center">
                    {filterCount}
                  </span>
                )}
              </button>
            </div>
            {/* Quick season group chips */}
            <div className="flex gap-2 mt-2">
              {SEASON_GROUP_CHIPS.map((opt) => {
                const isActive = activeGroup === opt.id;
                return (
                  <button
                    key={opt.id}
                    onClick={() => {
                      if (isActive) {
                        handleSeasonChange("");
                      } else {
                        handleSeasonChange(opt.id);
                      }
                    }}
                    className={`flex-1 min-h-11 py-1.5 rounded-full text-[12px] font-medium cursor-pointer font-sans transition-all ${
                      isActive
                        ? "bg-gold text-white border border-gold"
                        : "bg-white border border-border-light text-text-body hover:border-gold hover:text-gold active:bg-gold/5"
                    }`}
                  >
                    {opt.icon} {opt.label}
                  </button>
                );
              })}
            </div>

            {/* Mobile Filter Chips */}
            {hasAnyFilter && (
              <div className="flex flex-wrap gap-2 mt-3">
                {seasonFilter && (
                  <FilterChip
                    label={
                      SEASON_GROUP_CHIPS.find((g) => g.id === seasonFilter)
                        ?.label ?? seasonFilter
                    }
                    onRemove={() => handleSeasonChange("")}
                  />
                )}
                {roleChips.map((chip) => (
                  <FilterChip
                    key={chip.key}
                    label={chip.label}
                    onRemove={() => {
                      const next = new Set(roleIds);
                      for (const id of chip.leafIds) next.delete(id);
                      handleRolesChange(next);
                    }}
                  />
                ))}
                {locationChips.map((chip) => (
                  <FilterChip
                    key={chip.key}
                    label={chip.label}
                    onRemove={() => {
                      const next = new Set(locationIds);
                      for (const id of chip.leafIds) next.delete(id);
                      handleLocationsChange(next);
                    }}
                  />
                ))}
                {Array.from(perkIds).map((perkId) => {
                  const perk = LISTING_PERKS.find((p) => p.id === perkId);
                  if (!perk) return null;
                  return (
                    <FilterChip
                      key={perkId}
                      label={perk.label}
                      onRemove={() => handlePerkToggle(perkId)}
                    />
                  );
                })}
              </div>
            )}
          </div>

          {/* Desktop filter chips */}
          {hasAnyFilter && (
            <div className="hidden md:flex flex-wrap gap-2 mb-5">
              {seasonFilter && (
                <FilterChip
                  label={
                    SEASON_GROUP_CHIPS.find((g) => g.id === seasonFilter)?.label ??
                    seasonFilter
                  }
                  onRemove={() => handleSeasonChange("")}
                />
              )}
              {roleChips.map((chip) => (
                <FilterChip
                  key={chip.key}
                  label={chip.label}
                  onRemove={() => {
                    const next = new Set(roleIds);
                    for (const id of chip.leafIds) next.delete(id);
                    handleRolesChange(next);
                  }}
                />
              ))}
              {locationChips.map((chip) => (
                <FilterChip
                  key={chip.key}
                  label={chip.label}
                  onRemove={() => {
                    const next = new Set(locationIds);
                    for (const id of chip.leafIds) next.delete(id);
                    handleLocationsChange(next);
                  }}
                />
              ))}
              {Array.from(perkIds).map((perkId) => {
                const perk = LISTING_PERKS.find((p) => p.id === perkId);
                if (!perk) return null;
                return (
                  <FilterChip
                    key={perkId}
                    label={perk.label}
                    onRemove={() => handlePerkToggle(perkId)}
                  />
                );
              })}
            </div>
          )}

          {/* Desktop quick season group chips */}
          <div className="hidden md:flex gap-2 mb-4">
            {SEASON_GROUP_CHIPS.map((opt) => {
              const isActive = activeGroup === opt.id;
              return (
                <button
                  key={opt.id}
                  onClick={() => {
                    if (isActive) {
                      handleSeasonChange("");
                    } else {
                      handleSeasonChange(opt.id);
                    }
                  }}
                  className={`px-4 py-1.5 rounded-full text-[12.5px] font-medium cursor-pointer font-sans transition-all ${
                    isActive
                      ? "bg-gold text-white border border-gold"
                      : "bg-white border border-border-light text-text-body hover:border-gold hover:text-gold"
                  }`}
                >
                  {opt.icon} {opt.label}
                </button>
              );
            })}
          </div>

          <ListingInteractionGrid
            listings={listings}
            userRole={userRole}
            candidateState={candidateState}
            discoverySource="jobs_marketplace"
            emptyState={
              totalCount === 0 ? (
                <div className="flex justify-center py-12 md:py-20">
                  <div className="max-w-[520px] text-center">
                    <div className="w-14 h-14 rounded-full bg-gold/10 flex items-center justify-center mx-auto mb-5">
                      <svg
                        width="28"
                        height="28"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="1.6"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        className="text-gold"
                        aria-hidden="true"
                      >
                        <rect x="2" y="7" width="20" height="14" rx="2" ry="2" />
                        <path d="M16 21V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v16" />
                      </svg>
                    </div>
                    <h2 className="font-serif text-[24px] sm:text-[28px] text-navy mb-3">
                      A new way to find seasonal work
                    </h2>
                    <p className="text-[15px] text-text-body leading-[1.7] mb-8 max-w-[440px] mx-auto">
                      Employers can now post roles directly on PeakWave. Browse
                      listings and express your interest, or sign up and let
                      employers find you.
                    </p>
                    <div className="flex flex-col sm:flex-row gap-3 justify-center">
                      {userRole === "employer" ? (
                        <Link
                          href="/employer/listings/new"
                          className="bg-gold text-white px-7 py-3 rounded-sm text-[15px] font-semibold no-underline font-sans transition-all inline-flex min-h-[44px] items-center justify-center gap-2 hover:bg-gold-light hover:-translate-y-0.5"
                        >
                          Post a Listing
                        </Link>
                      ) : (
                        <>
                          <Link
                            href="/signup?intent=work&next=/jobs"
                            className="bg-gold text-white px-7 py-3 rounded-sm text-[15px] font-semibold no-underline font-sans transition-all inline-flex min-h-[44px] items-center justify-center gap-2 hover:bg-gold-light hover:-translate-y-0.5"
                          >
                            Sign Up as a Candidate
                          </Link>
                          <Link
                            href="/employer-signup"
                            className="text-navy px-7 py-3 rounded-sm text-[15px] font-semibold no-underline font-sans transition-all inline-flex min-h-[44px] items-center justify-center gap-2 border-[1.5px] border-navy/18 hover:border-navy hover:bg-navy/[0.03]"
                          >
                            Sign Up as an Employer
                          </Link>
                        </>
                      )}
                    </div>
                  </div>
                </div>
              ) : (
                <div className="text-center py-16">
                  <p className="text-[15px] text-text-muted mb-2">
                    No listings found matching your filters.
                  </p>
                  <button
                    type="button"
                    onClick={clearAllFilters}
                    className="min-h-[44px] text-gold text-sm font-semibold bg-transparent border-none cursor-pointer font-sans"
                  >
                    Clear filters
                  </button>
                </div>
              )
            }
          />
        </div>

        {/* Desktop Sidebar */}
        <aside
          id="job-filters"
          tabIndex={-1}
          aria-label="Filter jobs"
          className="hidden md:block md:col-start-1 md:row-start-1 bg-white border-r border-border-light p-7 sticky top-16 h-[calc(100vh-64px)] overflow-y-auto"
        >
          <FilterSidebar {...sidebarProps} />
        </aside>
      </div>

      {/* One banner shares its saved state across mobile and desktop. */}
      {showJobAlerts && listings.length > 0 && (
        <JobAlertSignup
          candidateJobAlertsEnabled={candidateState.jobAlertsEnabled}
          signupNext={jobsReturnPath}
          className={`px-3 py-3 md:ml-[280px] md:px-9 md:pb-7 ${listings.length >= 5 ? "sticky bottom-0 z-40 bg-warm-bg/95 backdrop-blur-sm border-t border-border-light md:static md:border-0" : ""}`}
        />
      )}

      {/* Navy CTA for employers */}
      <section className="bg-navy py-14 md:py-16 px-5">
        <div className="max-w-[560px] mx-auto text-center">
          <h2 className="font-serif text-[26px] md:text-[32px] font-normal text-white mb-3">
            Hiring seasonal staff?
          </h2>
          <p className="text-[15px] text-white/70 leading-[1.7] max-w-[440px] mx-auto mb-2">
            Post your jobs for free and reach {candidateCount.toLocaleString()}+ candidates actively looking for seasonal work. No fees, no commissions.
          </p>
          <p className="text-[14px] text-white/50 mb-7">
            100% free for employers. Always.
          </p>
          <Link
            href={userRole === "employer" ? "/employer/listings/new" : "/employer-signup"}
            className="bg-gold text-white px-8 py-3.5 rounded-sm text-[15px] font-semibold no-underline font-sans transition-all inline-flex items-center justify-center gap-2 hover:bg-gold-light hover:-translate-y-0.5"
          >
            Post a Job
          </Link>
        </div>
      </section>

      {/* Mobile Filter Drawer */}
      {mobileFiltersOpen && (
        <div className="fixed inset-0 z-[100] md:hidden">
          <div
            className="absolute inset-0 bg-black/40"
            onClick={() => setMobileFiltersOpen(false)}
          />
          <div className="absolute top-0 right-0 bottom-0 w-[85vw] max-w-[360px] bg-white p-5 overflow-y-auto animate-fade-in">
            <FilterSidebar
              {...sidebarProps}
              onClose={() => setMobileFiltersOpen(false)}
            />
          </div>
        </div>
      )}
    </>
  );
}


// ═══ FILTER CHIP ═══

function FilterChip({
  label,
  onRemove,
}: {
  label: string;
  onRemove: () => void;
}) {
  return (
    <button
      onClick={onRemove}
      className="inline-flex items-center gap-1.5 px-[11px] py-1 rounded-[20px] text-[12.5px] font-medium bg-cream text-navy border border-[rgba(0,0,0,0.10)] cursor-pointer transition-colors hover:bg-border font-sans"
    >
      {label}
      <span className="text-text-muted text-[14px] leading-none">&times;</span>
    </button>
  );
}
